import { useState, useCallback } from 'react';
import { api, AdminSubscriptionRecord, AdminSubscriptionListParams } from '@/lib/api';

export function SubscriptionManagementTab() {
  const [filters, setFilters] = useState<AdminSubscriptionListParams>({ limit: 25 });
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Action state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ userId: string; message: string } | null>(
    null,
  );

  // Tier change state
  const [tierChangeUserId, setTierChangeUserId] = useState<string | null>(null);
  const [newTier, setNewTier] = useState('standard');

  const search = useCallback(
    async (offset = 0) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminListSubscriptions({ ...filters, offset });
        setSubscriptions(result.subscriptions);
        setTotal(result.total);
        setFilters((f) => ({ ...f, offset }));
        setSearched(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const limit = filters.limit ?? 25;
  const offset = filters.offset ?? 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const handleAction = async (userId: string, action: 'cancel' | 'reactivate' | 'reconcile') => {
    setActionLoading(`${userId}-${action}`);
    setActionResult(null);
    try {
      if (action === 'cancel') {
        await api.adminCancelSubscription(userId);
        setActionResult({ userId, message: 'Subscription canceled' });
      } else if (action === 'reactivate') {
        await api.adminReactivateSubscription(userId);
        setActionResult({ userId, message: 'Subscription reactivated' });
      } else {
        await api.adminReconcileSubscription(userId);
        setActionResult({ userId, message: 'Subscription reconciled with Stripe' });
      }
      search(offset);
    } catch (err) {
      setActionResult({
        userId,
        message: `Error: ${err instanceof Error ? err.message : 'Failed'}`,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleTierChange = async (userId: string) => {
    setActionLoading(`${userId}-tier`);
    setActionResult(null);
    try {
      await api.adminChangeSubscriptionTier(userId, newTier);
      setActionResult({ userId, message: `Tier changed to ${newTier}` });
      setTierChangeUserId(null);
      search(offset);
    } catch (err) {
      setActionResult({
        userId,
        message: `Error: ${err instanceof Error ? err.message : 'Failed'}`,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      past_due: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      incomplete: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-muted text-muted-foreground'}`}
      >
        {status}
      </span>
    );
  };

  const tierBadge = (tier: string) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      standard: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      pro: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    };
    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[tier] || 'bg-muted text-muted-foreground'}`}
      >
        {tier}
      </span>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Tier</label>
            <select
              value={filters.tier || ''}
              onChange={(e) => setFilters((f) => ({ ...f, tier: e.target.value || undefined }))}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="canceled">Canceled</option>
              <option value="past_due">Past Due</option>
              <option value="trialing">Trialing</option>
            </select>
          </div>
          <button
            onClick={() => search(0)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {actionResult && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            actionResult.message.startsWith('Error')
              ? 'bg-destructive/10 border border-destructive/20 text-destructive'
              : 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
          }`}
        >
          <span className="font-mono text-xs">{actionResult.userId}</span>: {actionResult.message}
        </div>
      )}

      {searched && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {total} subscription{total !== 1 ? 's' : ''}
            </h3>
          </div>

          {subscriptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No subscriptions match your filters</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">User ID</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Tier</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Credits/mo
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Period End
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.subscriptionId} className="border-b border-border/50">
                        <td className="py-2 text-foreground font-mono text-xs truncate max-w-[120px]">
                          {sub.userId}
                        </td>
                        <td className="py-2">{tierBadge(sub.tier)}</td>
                        <td className="py-2">
                          {statusBadge(sub.status)}
                          {sub.cancelAtPeriodEnd && (
                            <span className="ml-1 text-xs text-yellow-600">(canceling)</span>
                          )}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {sub.monthlyCreditAllocation}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {sub.currentPeriodEnd
                            ? new Date(sub.currentPeriodEnd).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="py-2">
                          <div className="flex gap-1 flex-wrap">
                            {/* Change Tier */}
                            {tierChangeUserId === sub.userId ? (
                              <div className="flex gap-1 items-center">
                                <select
                                  value={newTier}
                                  onChange={(e) => setNewTier(e.target.value)}
                                  className="px-2 py-1 text-xs border border-border rounded bg-background text-foreground"
                                >
                                  <option value="standard">Standard</option>
                                  <option value="pro">Pro</option>
                                </select>
                                <button
                                  onClick={() => handleTierChange(sub.userId)}
                                  disabled={actionLoading === `${sub.userId}-tier`}
                                  className="px-2 py-1 text-xs font-medium text-primary-foreground bg-primary rounded hover:bg-primary/90 disabled:opacity-50"
                                >
                                  {actionLoading === `${sub.userId}-tier` ? '...' : 'OK'}
                                </button>
                                <button
                                  onClick={() => setTierChangeUserId(null)}
                                  className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setTierChangeUserId(sub.userId);
                                  setNewTier(sub.tier);
                                }}
                                className="px-2 py-1 text-xs text-primary hover:underline"
                              >
                                Change Tier
                              </button>
                            )}

                            {/* Cancel / Reactivate */}
                            {sub.status === 'active' && !sub.cancelAtPeriodEnd && (
                              <button
                                onClick={() => handleAction(sub.userId, 'cancel')}
                                disabled={actionLoading === `${sub.userId}-cancel`}
                                className="px-2 py-1 text-xs text-destructive hover:underline disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            )}
                            {sub.cancelAtPeriodEnd && (
                              <button
                                onClick={() => handleAction(sub.userId, 'reactivate')}
                                disabled={actionLoading === `${sub.userId}-reactivate`}
                                className="px-2 py-1 text-xs text-green-600 hover:underline disabled:opacity-50"
                              >
                                Reactivate
                              </button>
                            )}

                            {/* Reconcile */}
                            <button
                              onClick={() => handleAction(sub.userId, 'reconcile')}
                              disabled={actionLoading === `${sub.userId}-reconcile`}
                              className="px-2 py-1 text-xs text-muted-foreground hover:underline disabled:opacity-50"
                            >
                              Sync
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => search(Math.max(0, offset - limit))}
                      disabled={offset === 0 || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => search(offset + limit)}
                      disabled={offset + limit >= total || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
